import {Component, Input, NgZone, ChangeDetectorRef} from "@angular/core";

import {ModalConfig} from "../services/notification/modal.config";
import {EventBus} from "../event/EventBus";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";

import {ComponentBase} from "../base/ComponentBase";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {PipelineService, IGlossaryInfo} from "../services/pipeline/pipeline.service";
import {VocabularyPipelineService, VocabularyEntry} from "../services/pipeline/vocabulary.pipeline.service";

import {
    ISelection, IGlossaryEntry, ISyllables, IDefinition,
    IPartOfSpeech, IFact, IContext, ITranslation, ICredit, IVocabularyEntry
} from  "../api/data-model";
import {Subscription} from "rxjs";
import {ContentCursor} from "../services/content/content.cursor";
import {CssWord, CssSentence} from "../content/content-view.objects";
import {PipelineSaveEvent, PIPELINE_API_STATUS} from "./PipelineSaveEvent";
import {PipelineEvent, PIPELINE_STATUS} from "../event/PipelineEvent";
import {RaVersion} from "../services/configuration/ra.version";
import {UtilitiesService} from "../services/utilities.service";
import {HttpClient} from "@angular/common/http";
import {filter} from "rxjs/operators";

@Component({
    selector: "vocabulary-editor",
    templateUrl: "./vocabulary.editor.component.html",
    styleUrls: ["./pipeline.component.scss"]
})
export class VocabularyEditorComponent extends ComponentBase {

    public activateModal: boolean = false;
    public status:string = "";
    public saveInProgress:boolean = false;
    public newVocabularyEntry:VocabularyEntry;
    public selectedWord:CssWord = null;
    public vocabularyEntries:VocabularyEntry[];
    private scSubscription:Subscription;
    private selectionSubscription:Subscription;
    public useOldStyleUI = false;

    constructor (
        private http:HttpClient,
        private eventBus: EventBus,
        private modalConfig: ModalConfig,
        private logService: BugsnagLoggingService,
        private _ngZone: NgZone,
        private pipelineService:PipelineService,
        private contentCursor:ContentCursor,
        private ref: ChangeDetectorRef
    ) {
        super("VocabularyEditorComponent");
    }

    get selection(): ISelection {
        return this.pipelineService.currentSourceSelection;
    }

    get vocabularyService(): VocabularyPipelineService {
        return this.pipelineService.vocabularyPipelineService;
    }

    init(): void {
        if (this.selection) {
            this.initVocabularyUI();
        }
        if (this.useOldStyleUI) {
            this.scSubscription = this.contentCursor.wordChanged.subscribe(word => {
                this.logService.log("WORD CLICKED: " + word.word + " window selection: " + window.getSelection());
                this.selectedWord = word;
                if (word.glossary) {
                    let glossaryEntry: IGlossaryEntry = this.pipelineService.getStoredGlossaryFromID(word.glossary);
                    let vocabularyEntry: VocabularyEntry = new VocabularyEntry(
                        {word: "", glossary: "", translations: {}});
                    vocabularyEntry.initFromSelection(glossaryEntry, word.translations[VocabularyEntry.LANGUAGE]);
                    vocabularyEntry.isEditable = true;
                    this.refreshEditableEntry(vocabularyEntry);
                } else {
                    let searchTerm = word.word;
                    this.doSearch(searchTerm);
                }
            });
            this.subscriptions.push(this.scSubscription);
        }
        this.selectionSubscription = this.eventBus.pipe(filter((e:PipelineEvent) =>
                             (e.type === PipelineEvent.TYPE) &&
                             (e.status === PIPELINE_STATUS.selectionUpdated) ))
            .subscribe((e:PipelineEvent) => {
                console.log("updating vocab UI for new selection");
                this.initVocabularyUI();
            });
        this.subscriptions.push(this.selectionSubscription);
    }

    private initVocabularyUI() {
        if (!this.selection.vocabularyEntries) {
            this.selection.vocabularyEntries = [];
        }
        this.useOldStyleUI = this.vocabularyService.useOldStyleUI(this.selection.metaData.contentVersion);
        if (this.useOldStyleUI) {
            this.resetVocabularyOldStyleUI();
        } else {
            this.getVocabularyFromLinkedGlossary();
        }
    }

    public doSearch(searchTerm: string): void {
        this.logService.log("returned search term:" + searchTerm + ":");
        let glossaryEntries = this.pipelineService.getStoredGlossary(searchTerm);
        let vocabularyEntry = this.vocabularyService.getVocabularyEntryFromGlossary(
            searchTerm, glossaryEntries);
        this.refreshEditableEntry(vocabularyEntry);
    }

    cleanup(): void {
        if ( this.scSubscription) {
            this.scSubscription.unsubscribe();
        }
        if ( this.selectionSubscription) {
            this.selectionSubscription.unsubscribe();
        }
    }

    public refreshEditableEntry(entry: VocabularyEntry):void {
        if (this.newVocabularyEntry.isEditable) {
            this.newVocabularyEntry = entry;
        } else {
            this.vocabularyEntries.forEach((vocabEntry, index) => {
                if (vocabEntry.isEditable) {
                    this.vocabularyEntries[index] = entry;
                }
            });
        }
    }

    public updateEditableEntry(entry: VocabularyEntry):void {
        let selectionVocabularyEntry:IVocabularyEntry = {glossary: entry.glossary,
            word: entry.word,
            translations: entry.translations};
        console.log("updating entry with: " + JSON.stringify(selectionVocabularyEntry));
        if (this.newVocabularyEntry.isEditable) {
            console.log("updating/adding new ");
            this.selection.vocabularyEntries.push(selectionVocabularyEntry);
        } else {
            this.vocabularyEntries.forEach((vocabEntry, index) => {
                if (vocabEntry.isEditable) {
                    console.log("updating index " + index.toString());
                    this.selection.vocabularyEntries[index] = selectionVocabularyEntry;
                }
            });
        }
        this.resetVocabularyOldStyleUI();
    }

    public resetVocabularyOldStyleUI():void {
        this.vocabularyEntries = this.vocabularyService.getVocabularyEntries(this.selection.vocabularyEntries);
        console.log(JSON.stringify(this.vocabularyEntries));
        this.newVocabularyEntry = new VocabularyEntry({word: "__NEW__", glossary: "", translations: null});
        this.newVocabularyEntry.isEditable = true;
        this.ref.detectChanges();
    }

    private getVocabularyFromLinkedGlossary():void {
        this.vocabularyEntries = this.vocabularyService.getLinkedVocabulary(this.selection);
        console.log(JSON.stringify(this.vocabularyEntries));
        this.ref.detectChanges();
    }

    public replaceVocabularyEntry(idx: string): void {
        let index = Number(idx);
        this.newVocabularyEntry.isEditable = false;
        this.vocabularyEntries.forEach((entry, i) => {
            if ((index >= 0) && (index === i)) {
                entry.isEditable = true;
            } else {
                entry.isEditable = false;
            }
        });
        this.ref.detectChanges();
    }

    public deleteVocabularyEntry(idx: string): void {
        let index = Number(idx);
        this.vocabularyEntries.splice(index, 1);
        this.selection.vocabularyEntries.splice(index, 1);
        this.ref.detectChanges();
    }

    public cancelVocabularyEntry(idx: string): void {
        this.resetVocabularyOldStyleUI();
        this.ref.detectChanges();
    }

    public saveSelection(): void {
        this.saveInProgress = true;
        this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.start));
        if (!this.useOldStyleUI) {
            this.selection.vocabularyEntries = [];
            this.vocabularyEntries.forEach((vocabEntry) => {
                if (vocabEntry._isSelected) {
                    this.selection.vocabularyEntries.push({glossary: vocabEntry.glossary, word: vocabEntry.word,
                                                              translations: vocabEntry.translations});
                }
            });
        }
        this.selection.vocabularyEntries.forEach((vocabEntry) => {
            let phrase = UtilitiesService.convertPunctuationToASCII(vocabEntry.word).trim().split(" ");
            if ((phrase.length > 1) || (vocabEntry.word !== phrase[0])) {
                vocabEntry.dictionary = [phrase.join(" ")];
            }
        });
        this.pipelineService.saveSourceContent().subscribe(result => {
            this.logService.log("saved uninflated");
            this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.finish,
                this.pipelineService.currentSourceSelection));
            this.ref.detectChanges();
            this.saveInProgress = false;
        }, (error) => {
            alert("ERROR: selection save (vocabulary entries/ word wall) failed! " + error.status);
        });
    }

    public makeActive():void {
        this.init();
    }

    public makeInactive():void {
        this.cleanup();
    }

}
